import get from 'lodash.get';

// Load global environment config
import env from '#shared/config/env.js';
import isObject from '#shared/util/isObject.js';
import serializeUrl from '#shared/util/serializeURL.js';

import getHPPStyleObject from './getHPPStyleObject.js';

/**
 * Asynchronously attach and load external script to the document
 * @param {string} src Source of the library
 * @returns {Promise<void>}
 */
const loadExternalScript = (src) => new Promise((resolve, reject) => {
	document.body.appendChild(Object.assign(document.createElement('script'), {
		src,
		defer: true,
		onload: resolve,
		onerror: reject
	}));
});

/**
 * Export the Vue plugin install method
 * @param {object} Vue Vue instance
 */
export const install = (Vue) => { // eslint-disable-line max-lines-per-function

	/**
	 * Create global "providersLibrary" property
	 * @param Vue
	 * @note We are using Vue here so the property is reactive and frontend can react to changes (load, etc.)
	 */
	Vue.prototype.providersLibrary = new Vue({
		data: () => ({
			store: null,
			provider: null,
			payment: null,
			fingerprint: null
		}),
		computed: {
			selectedPayment() {
				return this.store?.state?.selectedPayment ?? null;
			},
			isRtl() {
				return this.store?.state?.theme.variables.isRtl ?? null;
			},
			isCreditCard() {
				return this.selectedPayment?.type === 'creditCard';
			},
			isDirectDebit() {
				return this.selectedPayment?.type === 'directDebit';
			},
			isBancontact() {
				return this.selectedPayment?.type === 'bancontact';
			}
		},
		watch: {
			store(updatedStore) {
				const { name, project, hppVendorId } = updatedStore.state.theme.provider;
				this.provider = {
					name,
					project,
					hppVendorId,
					credentials: env.providers[name].credentials[project],
					variables: env.providers[name].variables[project]
				};
			}
		},
		methods: {
			catchPaymentSource(paymentSource = {}) {
				// If paymentSource contains "sourceString", it means that it's fulfilled with all information
				if (!isObject(paymentSource) || isObject(paymentSource) && paymentSource.sourceString) {
					// Track successful source creation
					if (isObject(paymentSource)) {
						this.tracking.paymentSourceCreation({
							selectedPayment: this.selectedPayment,
							url: this.store.state?.url
						});
					}
					return Promise.resolve(paymentSource || null);
				}
				// Handle HPP callbacks in Promise ways
				return new Promise((resolve, reject) => {
					const onHppResponseCallback = (method) => (response) => {
						const callSuccess = (payload) => resolve({
							...paymentSource,
							...payload
						});
						const callFailure = (payload) => reject(payload);
						const hppMethodCallback = {
							OnErrorHPP: () => {
								this.tracking.paymentSourceCreation({
									selectedPayment: this.selectedPayment,
									url: this.store.state?.url
								}, {
									description: response.message,
									hasError: true
								});
								callFailure(response);
							},
							OnPayPalRedirectHPP: () => callSuccess({
								redirect: {
									redirectUrl: response
								}
							}),
							onCompleteHPP: () => {
								this.tracking.paymentSourceCreation({
									selectedPayment: this.selectedPayment,
									url: this.store.state?.url
								});
								callSuccess({
									...response,
									sourceString: response.sourceId
								});
							}
						}[method];
						if (typeof hppMethodCallback === 'function') {
							hppMethodCallback();
						} else {
							callFailure(`Undefined HPP method ${method}`);
						}
					};
					window.OnErrorHPP = onHppResponseCallback('OnErrorHPP');
					window.onCompleteHPP = onHppResponseCallback('onCompleteHPP');
					window.OnPayPalRedirectHPP = onHppResponseCallback('OnPayPalRedirectHPP');
				});
			},
			async authenticateSource() {
				if (this.isCreditCard) {
					const [updateSourceIdResponse] = await Promise.all([
						this.catchPaymentSource(),
						window.updateSourceId(this.selectedPayment.sourceId)
					]);
					return updateSourceIdResponse.status?.statusMessage === 'SUCCESS' ? {
						paymentOptionId: this.selectedPayment.sourceId,
						sourceString: updateSourceIdResponse.sourceId
					} : null;
				}
				return {};
			},
			handleNextAction(nextAction) {
				return new Promise((resolve, reject) => {
					// Global next action timeout so it at least fails after some time (5 minutes)
					const nextActionTimeout = setTimeout(() => reject('handleNextActionTimeout'), 300000);
					// Show payment iframe modal window and load the inner iframe
					this.store.commit('SET_PAYMENT_GATEWAY_PROVIDER_URL', nextAction);
					this.store.commit('SET_MODAL', 'paymentIframe');
					// Listen for messages and wait for the payment iframe status message
					const scopedStore = this.store;
					window.addEventListener('message', function handleNextActionMessage(event) {
						if (event.data.type === 'checkout-payment_provider-status') {
							const paymentProviderStatus = event.data.payload || 'failure';
							if (/success/i.test(paymentProviderStatus)) {
								resolve(paymentProviderStatus);
							} else {
								reject(paymentProviderStatus);
							}
							scopedStore.commit('SET_MODAL', '');
							scopedStore.commit('SET_PAYMENT_GATEWAY_PROVIDER_URL', '');
							clearTimeout(nextActionTimeout);
							this.removeEventListener('message', handleNextActionMessage);
						}
					});
				});
			},
			loadPayment({ isFirstLoad = false } = {}) {
				// Load HPP in case the payment object does not exist or when loadPayment is being called again (not first time)
				if (this.store && (!this.payment || !isFirstLoad)) {
					const hppGuestPayload = {
						access_token: this.store.state.payment.providerPaymentToken,
						vendorCode: this.provider.hppVendorId,
						env: this.provider.variables.servicesEnv,
						locale: this.store.state.theme.provider.locale,
						width: '100%',
						height: '50',
						isDarkTheme: this.store.state.theme.brand === 'avg' && this.store.state.theme.name === 'ipm',
						isSepaDD: this.isDirectDebit,
						isBancontact: this.isBancontact,
						localization: {
							ccNumber: {
								label: get(this.store, 'state.localization.form_cardNumber', 'Card Number')
							},
							ccExpDate: {
								label: get(this.store, 'state.localization.form_cardExpirationPlaceholder', 'MM/YY')
							},
							ccCVC: {
								label: get(this.store, 'state.localization.form_cardSecurityCodePlaceholder', 'CVC')
							},
							ddIBAN: {
								label: get(this.store, 'state.localization.form_iban', 'IBAN')
							}
						}
					};
					const hppAuthenticatedPayload = {
						...hppGuestPayload,
						height: '62',
						isCVCOnly: this.isCreditCard,
						card_type: get(this.store, 'state.payment.name', '')
					};
					this.payment = window.Sym.HPP;
					setTimeout(() => {
						const isAuthenticated = get(this.store, 'state.order.isAuthenticated', false) && !get(this.store, 'state.isNewMethod', false);
						const hppPayload = isAuthenticated ? hppAuthenticatedPayload : hppGuestPayload;
						hppPayload.style = getHPPStyleObject(this.selectedPayment?.type, this.isRtl);
						// HPP provider elements' ids based on the selected payment method
						const hppElementIds = {
							hpp_iban: this.isDirectDebit,
							hpp_bancontact: this.isBancontact,
							hpp_creditCard: true // Default value
						};
						// Return the first true value else default value (hpp_creditCard)
						const hppElementId = Object.keys(hppElementIds).find((elementId) => hppElementIds[elementId] === true);
						window.loadHPP(hppElementId, hppPayload);
					}, 0);
				}
			},
			async loadFingerprint() {
				if (!this.fingerprint) {
					const fingerprintLibraryParams = serializeUrl({
						org_id: this.provider.variables.fingerprintOrganizationId,
						session_id: this.store.state.order.entityCode + this.store.state.order.guid
					});
					const fingerprintLibrarySrc = `${this.provider.variables.fingerprintLibrary}?${fingerprintLibraryParams}`;
					await loadExternalScript(fingerprintLibrarySrc);
					this.fingerprint = {};
				}
			},
			async load(store) {
				this.store = store;
				await this.$nextTick();
				this.loadPayment({
					isFirstLoad: true
				});
				await this.loadFingerprint();
			}
		}
	});
};

export default {
	install
};
